export type SupportedCurrency = 'USD' | 'EUR' | 'GBP' | 'INR' | 'CAD' | 'AUD' | 'JPY' | 'AED' | 'SGD';

export interface CurrencyConfig {
  code: SupportedCurrency;
  symbol: string;
  rate: number;
  label: string;
}

export const DEFAULT_CURRENCY_RATES: Record<SupportedCurrency, number> = {
  USD: 1.0,
  EUR: 0.92,
  GBP: 0.78,
  INR: 86.85, // Current standard conversion: $1 USD = ~86.85 INR
  CAD: 1.38,
  AUD: 1.52,
  JPY: 155.0,
  AED: 3.67,
  SGD: 1.34
};

export const SUPPORTED_CURRENCIES: SupportedCurrency[] = ['USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD', 'JPY', 'AED', 'SGD'];

export function getCurrencyCookie(): SupportedCurrency | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)harrydesk_currency=([^;]+)/);
    const code = match ? decodeURIComponent(match[1]).toUpperCase() as SupportedCurrency : null;
    return (code && SUPPORTED_CURRENCIES.includes(code)) ? code : null;
  } catch {
    return null;
  }
}

export function setCurrencyCookie(currency: SupportedCurrency) {
  try {
    document.cookie = `harrydesk_currency=${encodeURIComponent(currency)}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {}
}

export interface DetectedLocationResult {
  currency: SupportedCurrency;
  country: string;
  city?: string;
  source: 'manual' | 'cached' | 'api' | 'locale' | 'precise' | 'fallback';
}

/**
 * Synchronous client-side location & currency detection based on Browser Intl TimeZone, Language & Cookies.
 * Executes in 0ms (synchronous) so the user immediately sees prices in their local currency on 1st render.
 */
export function detectLocalLocationAndCurrency(): DetectedLocationResult {
  try {
    // 1. If user previously manually picked a currency in cookie or localStorage, honor their choice
    const isManual = localStorage.getItem('harrydesk_user_manually_set_currency') === 'true';
    const cookieCur = getCurrencyCookie();
    const savedCurrency = (cookieCur || localStorage.getItem('harrydesk_selected_currency')) as SupportedCurrency;
    const savedCountry = localStorage.getItem('harrydesk_saved_country');
    const savedCity = localStorage.getItem('harrydesk_saved_city');

    if ((isManual || cookieCur) && savedCurrency && SUPPORTED_CURRENCIES.includes(savedCurrency)) {
      return {
        currency: savedCurrency,
        country: savedCountry || 'Custom Selected',
        city: savedCity || undefined,
        source: 'manual'
      };
    }

    // 2. High-precision Intl TimeZone & Locale detection
    const timeZone = (Intl.DateTimeFormat().resolvedOptions().timeZone || '').toLowerCase();
    const languages = (navigator.languages && navigator.languages.length ? navigator.languages.join(',') : navigator.language || '').toLowerCase();
    
    // INDIA & South Asia -> INR (₹)
    if (
      timeZone.includes('kolkata') || 
      timeZone.includes('calcutta') || 
      timeZone.includes('delhi') || 
      timeZone.includes('mumbai') || 
      timeZone.includes('chennai') || 
      timeZone.includes('asia/colombo') || 
      timeZone.includes('asia/kathmandu') ||
      languages.includes('en-in') || 
      languages.includes('hi') || 
      languages.includes('ta') || 
      languages.includes('te') || 
      languages.includes('mr') || 
      languages.includes('gu') || 
      languages.includes('bn') || 
      languages.includes('pa') ||
      languages.includes('-in')
    ) {
      return { currency: 'INR', country: 'India', source: 'locale' };
    }

    // UNITED KINGDOM -> GBP (£)
    if (
      timeZone.includes('london') || 
      timeZone.includes('europe/london') || 
      timeZone.includes('europe/belfast') || 
      timeZone.includes('europe/gibraltar') ||
      languages.includes('en-gb') || 
      languages.includes('-gb') || 
      languages.includes('en-uk')
    ) {
      return { currency: 'GBP', country: 'United Kingdom', source: 'locale' };
    }

    // EUROZONE -> EUR (€)
    if (
      timeZone.includes('paris') || 
      timeZone.includes('berlin') || 
      timeZone.includes('rome') || 
      timeZone.includes('madrid') || 
      timeZone.includes('amsterdam') || 
      timeZone.includes('brussels') || 
      timeZone.includes('vienna') || 
      timeZone.includes('dublin') || 
      timeZone.includes('lisbon') || 
      timeZone.includes('helsinki') || 
      timeZone.includes('athens') || 
      timeZone.includes('prague') || 
      timeZone.includes('warsaw') || 
      timeZone.includes('budapest') || 
      timeZone.includes('stockholm') || 
      timeZone.includes('oslo') || 
      timeZone.includes('copenhagen') || 
      timeZone.includes('europe') ||
      languages.includes('de') || 
      languages.includes('fr') || 
      languages.includes('it') || 
      languages.includes('es') || 
      languages.includes('nl') || 
      languages.includes('pt') || 
      languages.includes('el') || 
      languages.includes('fi') || 
      languages.includes('ga')
    ) {
      return { currency: 'EUR', country: 'Europe', source: 'locale' };
    }

    // CANADA -> CAD (CA$)
    if (
      timeZone.includes('toronto') || 
      timeZone.includes('vancouver') || 
      timeZone.includes('montreal') || 
      timeZone.includes('edmonton') || 
      timeZone.includes('winnipeg') || 
      timeZone.includes('halifax') || 
      timeZone.includes('america/st_johns') ||
      languages.includes('en-ca') || 
      languages.includes('fr-ca') ||
      languages.includes('-ca')
    ) {
      return { currency: 'CAD', country: 'Canada', source: 'locale' };
    }

    // AUSTRALIA & NZ -> AUD (A$)
    if (
      timeZone.includes('australia') || 
      timeZone.includes('sydney') || 
      timeZone.includes('melbourne') || 
      timeZone.includes('brisbane') || 
      timeZone.includes('perth') || 
      timeZone.includes('adelaide') || 
      timeZone.includes('hobart') || 
      timeZone.includes('darwin') ||
      timeZone.includes('auckland') ||
      languages.includes('en-au') || 
      languages.includes('-au') ||
      languages.includes('en-nz')
    ) {
      return { currency: 'AUD', country: 'Australia', source: 'locale' };
    }

    // JAPAN -> JPY (¥)
    if (
      timeZone.includes('tokyo') || 
      timeZone.includes('asia/tokyo') || 
      languages.includes('ja') || 
      languages.includes('-jp')
    ) {
      return { currency: 'JPY', country: 'Japan', source: 'locale' };
    }

    // UAE -> AED
    if (
      timeZone.includes('dubai') ||
      timeZone.includes('asia/dubai') ||
      languages.includes('ar-ae') ||
      languages.includes('-ae')
    ) {
      return { currency: 'AED', country: 'United Arab Emirates', source: 'locale' };
    }

    // SINGAPORE -> SGD (S$)
    if (
      timeZone.includes('singapore') ||
      timeZone.includes('asia/singapore') ||
      languages.includes('en-sg') ||
      languages.includes('-sg')
    ) {
      return { currency: 'SGD', country: 'Singapore', source: 'locale' };
    }

    // Check if previously detected without manual override
    if (savedCurrency && SUPPORTED_CURRENCIES.includes(savedCurrency)) {
      return {
        currency: savedCurrency,
        country: savedCountry || 'United States',
        city: savedCity || undefined,
        source: 'cached'
      };
    }
  } catch (err) {
    console.warn('Locale currency resolution error:', err);
  }

  // DEFAULT -> USD ($)
  return { currency: 'USD', country: 'United States', source: 'fallback' };
}

/**
 * Asynchronous background fetch to refine city/country via API without blocking page load.
 */
export async function fetchRemoteLocation(): Promise<DetectedLocationResult | null> {
  // 1. Try our internal server endpoint first (fastest and zero-CORS risk)
  try {
    const res = await fetch('/api/geo/lookup', { cache: 'no-store' });
    if (res.ok) {
      const data = await res.json();
      if (data && data.currency && SUPPORTED_CURRENCIES.includes(data.currency)) {
        return {
          currency: data.currency as SupportedCurrency,
          country: data.country || 'United States',
          city: data.city || undefined,
          source: 'api'
        };
      }
    }
  } catch (err) {
    // Fail silently into client-side timezone-based fallback
  }

  // 2. Secondary public IP fallback if running locally or behind transparent proxy
  try {
    const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(2000) });
    if (res.ok) {
      const data = await res.json();
      const code = (data.country_code || '').toUpperCase();
      let currency: SupportedCurrency = 'USD';
      if (code === 'IN') currency = 'INR';
      else if (code === 'GB' || code === 'UK') currency = 'GBP';
      else if (['DE', 'FR', 'IT', 'ES', 'NL', 'BE', 'AT', 'FI', 'IE', 'PT', 'GR', 'LU', 'SK', 'SI', 'CY', 'MT', 'EE', 'LV', 'LT', 'HR'].includes(code)) currency = 'EUR';
      else if (code === 'CA') currency = 'CAD';
      else if (code === 'AU' || code === 'NZ') currency = 'AUD';
      else if (code === 'JP') currency = 'JPY';
      else if (code === 'AE') currency = 'AED';
      else if (code === 'SG') currency = 'SGD';

      return {
        currency,
        country: data.country_name || code,
        city: data.city || undefined,
        source: 'api'
      };
    }
  } catch {
    // Fail silently
  }

  return null;
}
